import {DeepPartial} from '@appscience/utils'
import {TableNode} from '@table-library/react-table-library'
import {SelectClickTypes, SelectTypes, useRowSelect} from '@table-library/react-table-library/select'
import {Select as SelectData} from '@table-library/react-table-library/types/select'
import {mergeDeepRight} from 'ramda' //TODO:избавиться от зависимости от ramda

interface SelectState {
  ids: string[],
  all?: boolean,
  none?: boolean,
}

type DataTableSelectType = 'none'|'single'|'multi'

interface CommonSelectOptions {
  type: DataTableSelectType,
  enableRowSelect: boolean,
  checkbox: {
    show: boolean,
    pinLeft: boolean
  }
}

interface SingleSelectProps extends DeepPartial<CommonSelectOptions> {
  type: 'single',
  onChange?: (id: string|null) => void,
}

interface MultiSelectProps extends DeepPartial<CommonSelectOptions> {
  type: 'multi',
  onChange?: (ids: Array<string>) => void,
}

interface NoneSelectProps extends DeepPartial<CommonSelectOptions> {
  type: 'none',
}

export type DataTableSelectProps = (SingleSelectProps|MultiSelectProps|NoneSelectProps) & {
  selectedRowIds?: Array<string>,
}

export interface DataTableSelectContext extends CommonSelectOptions {
  onToggle: (id: string) => void,
  onToggleAll: () => void,
  state: SelectState,
}

interface ReturnValue {
  tableSelect: SelectData,
  selectContext: DataTableSelectContext,
}

export function useDataTableRowSelect<ITEM extends TableNode>(tableData: { nodes: Array<ITEM> }, initData?: DataTableSelectProps): ReturnValue|undefined {
  const selectOptions: CommonSelectOptions = initData
    ? mergeDeepRight(getDefaultSelectProps(), initData)
    : getDefaultSelectProps()

  const tableSelect = useRowSelect(tableData, {
    state: { ids: initData?.selectedRowIds || [] },
    onChange: (action, state) => {
      initData?.type !== 'none' && initData?.onChange && initData.onChange(
        initData?.type === 'single' ? state.id : state.ids,
      )
    },
  }, {
    rowSelect: selectOptions.type === 'multi' ? SelectTypes.MultiSelect
      : selectOptions.type === 'single' ? SelectTypes.SingleSelect : undefined,
    buttonSelect: selectOptions.type === 'multi'
      ? SelectTypes.MultiSelect
      : selectOptions.type === 'single' ? SelectTypes.SingleSelect : undefined,
    clickType: getClickType(selectOptions),
  })

  if (selectOptions.type === 'none') {
    return undefined
  }

  const selectedIds: Array<string> = selectOptions.type === 'single'
    ? tableSelect.state.id ? [tableSelect.state.id] : []
    : tableSelect.state.ids

  const selectState = selectOptions.type === 'single'
    ? {
      ...tableSelect.state,
      ids: selectedIds,
      none: selectedIds.length === 0,
    } as SelectState
    : tableSelect.state as SelectState

  return {
    tableSelect,
    selectContext: {
      ...selectOptions,
      onToggle: selectOptions.type === 'single'
        ? tableSelect.fns.onToggleByIdExclusively
        : tableSelect.fns.onToggleById,
      onToggleAll: () => {
        if (selectOptions.type === 'multi') {
          tableSelect.fns.onToggleAll({})
          return
        }
        if (selectState.ids.length > 0) {
          tableSelect.fns.onRemoveAll()
        }
      },
      state: selectState as SelectState,
    },
  }
}

function getClickType(options: CommonSelectOptions) {
  if (options.type === 'none') {
    return undefined
  }
  return options.enableRowSelect
    ? SelectClickTypes.RowClick
    : options.checkbox.show ? SelectClickTypes.ButtonClick : undefined
}

function getDefaultSelectProps(): CommonSelectOptions {
  return {
    type: 'multi',
    enableRowSelect: true,
    checkbox: {
      show: true,
      pinLeft: true,
    },
  }
}